/**
 * Shared controls for the Configure AI page's profile-based access design:
 *
 *  - <ProfileCheckboxes> — pick the functional roles ("profiles") a grant
 *    applies to, from the project's own role catalogue, with an escape hatch
 *    for a role that doesn't exist yet (e.g. one the deployed app will define).
 *  - <AssistantPreviewModal> — run the real Assistant right here, AS a chosen
 *    profile, so the designer can verify the gating they just configured
 *    without deploying and signing in as somebody else.
 *
 * The preview runs the SAME `/api/assistant/*` endpoints the in-app Run view
 * uses; the backend honours `as_roles` only for a caller with project edit
 * rights, and it can only narrow what that caller could already see.
 */
import { useEffect, useMemo, useState } from 'react'
import { Bot, Plus, X } from 'lucide-react'
import { AssistantChat } from '@/assistant/AssistantChat'

const box: React.CSSProperties = {
  padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 6,
  fontSize: 14, background: 'var(--color-bg)', color: 'var(--color-text)', outline: 'none',
}

// Reserved runtime roles a designer will nearly always want to reach for:
// the public chatbot's anonymous visitor, and the runtime administrator.
export const RESERVED_PROFILES = ['func-admin', 'site-visitor']

/** The union of the given roles, the reserved ones, and any role already
 *  referenced by the config (so a role removed from the catalogue is still
 *  visible — and removable — rather than silently dropped).
 *
 *  `includeReserved: false` for a list that must stay EXACTLY within a grant
 *  (e.g. the per-intent picker inside a restricted Assistant grant), where
 *  offering func-admin/site-visitor would suggest a rule that can never fire. */
export function useProfileChoices(
  funcRoles: string[], referenced: string[], includeReserved = true,
): string[] {
  return useMemo(() => {
    const seen = new Map<string, string>()
    for (const r of [...funcRoles, ...(includeReserved ? RESERVED_PROFILES : []), ...referenced]) {
      const key = (r || '').trim().toLowerCase()
      if (key && !seen.has(key)) seen.set(key, r.trim())
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- array identities change every render; content is what matters
  }, [funcRoles.join(','), referenced.join(','), includeReserved])
}

export function ProfileCheckboxes({
  choices, value, onChange, disabled, emptyHint,
}: {
  choices: string[]
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
  emptyHint?: string
}) {
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const has = (r: string) => value.some((v) => v.toLowerCase() === r.toLowerCase())

  const toggle = (r: string) => {
    onChange(has(r) ? value.filter((v) => v.toLowerCase() !== r.toLowerCase()) : [...value, r])
  }
  const addCustom = () => {
    const r = draft.trim()
    if (r && !has(r)) onChange([...value, r])
    setDraft(''); setAdding(false)
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      {choices.length === 0 && !value.length && (
        <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
          {emptyHint || 'No profiles defined yet — add one below.'}
        </span>
      )}
      {choices.map((r) => (
        <label key={r} style={{
          display: 'flex', alignItems: 'center', gap: 5, fontSize: 13,
          padding: '3px 8px', borderRadius: 999,
          border: `1px solid ${has(r) ? 'var(--color-accent)' : 'var(--color-border)'}`,
          background: has(r) ? 'color-mix(in srgb, var(--color-accent) 12%, transparent)' : 'transparent',
          cursor: disabled ? 'default' : 'pointer',
        }}>
          <input type="checkbox" checked={has(r)} disabled={disabled} onChange={() => toggle(r)} />
          {r}
        </label>
      ))}
      {!disabled && (adding ? (
        <span style={{ display: 'inline-flex', gap: 4 }}>
          <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addCustom(); if (e.key === 'Escape') { setDraft(''); setAdding(false) } }}
            placeholder="profile name" style={{ ...box, width: 140, padding: '3px 8px', fontSize: 13 }} />
          <button onClick={addCustom} style={{ ...box, padding: '3px 8px', cursor: 'pointer' }}>Add</button>
        </span>
      ) : (
        <button onClick={() => setAdding(true)} title="Add a profile that isn't in the list yet"
          style={{ ...box, padding: '3px 8px', fontSize: 13, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <Plus size={12} /> profile
        </button>
      ))}
    </div>
  )
}

/** Full-screen-ish preview of the configured Assistant, optionally AS one of
 *  the granted profiles. Save before previewing — it reads the SAVED config. */
export function AssistantPreviewModal({
  project, open, onClose, profiles, displayName,
}: {
  project: string
  open: boolean
  onClose: () => void
  profiles: string[]
  displayName?: string
}) {
  // '' = preview as myself (the designer's own access).
  const [asProfile, setAsProfile] = useState('')
  // Remounts the chat when the simulated profile changes, so a fresh session
  // is fetched with the new gating rather than continuing the old one.
  const [sessionKey, setSessionKey] = useState(0)

  useEffect(() => { if (open) { setAsProfile(''); setSessionKey((k) => k + 1) } }, [open])

  if (!open) return null
  const asRoles = asProfile ? [asProfile] : undefined

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 'min(760px, 96vw)', height: 'min(720px, 92vh)', background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)', borderRadius: 14, overflow: 'hidden',
        display: 'flex', flexDirection: 'column', color: 'var(--color-text)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: '1px solid var(--color-border)' }}>
          <Bot size={17} style={{ color: 'var(--color-accent)' }} />
          <strong style={{ fontSize: 16 }}>Preview — {displayName || 'Assistant'}</strong>
          <label style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            Preview as
            <select value={asProfile} style={{ ...box, padding: '4px 8px', fontSize: 13 }}
              onChange={(e) => { setAsProfile(e.target.value); setSessionKey((k) => k + 1) }}>
              <option value="">Myself (full access)</option>
              {profiles.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <button onClick={onClose} title="Close" style={{ ...box, padding: 6, cursor: 'pointer' }}><X size={15} /></button>
        </div>
        <div style={{ padding: '8px 14px', fontSize: 13, color: 'var(--color-text-secondary)', borderBottom: '1px solid var(--color-border)' }}>
          Runs against your preview data with the SAVED configuration — save your changes first to preview them.
          {asProfile && <> Answering as <b>{asProfile}</b>: only the Business Areas and intents that profile may access are offered.</>}
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          <AssistantChat key={`${sessionKey}:${asProfile}`} project={project} asRoles={asRoles} height="100%" />
        </div>
      </div>
    </div>
  )
}
